import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, signal, WritableSignal } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { FaIconLibrary } from "@fortawesome/angular-fontawesome";
import {
  faExclamationCircle,
  faEye,
  faEyeSlash,
  faTimes,
  faSignOutAlt,
  faPersonCircleCheck
} from '@fortawesome/pro-light-svg-icons';
import { HttpErrorResponse } from "@angular/common/http";
import { MatButtonModule } from "@angular/material/button";
import { CapsLockNoticeComponent } from "../../../../../shared/components/caps-lock-notice/caps-lock-notice.component";
import { MatFormFieldModule } from "@angular/material/form-field";
import { InvalidCredentialsNoticeComponent } from "../../ui/invalid-credentials-notice/invalid-credentials-notice.component";
import { IconModule } from "@ogs/ngx-m3-components";
import { HeadlineTitleComponent } from "../../ui/headline/components/title/headline-title.component";
import { HeadlineDescriptionComponent } from "../../ui/headline/components/description/headline-description.component";
import { HeadlineComponent } from "../../ui/headline/headline.component";
import { MatInputModule } from "@angular/material/input";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { AuthService } from "@features/authenticate/services/auth.service";
import { CORE_ROUTES } from "@core/constants/routes.constant";
import { AUTHENTICATE_ROUTES } from "@features/authenticate/constants/routes.constant";
import { provideTranslationScope, TranslationPipeModule } from "@ogs-gmbh/ngx-translate";
import { NotifyChipComponent } from '@shared/components/notify-chip/notify-chip.component';
import {
  NotifyChipValueComponent
} from '@shared/components/notify-chip/components/notify-chip-value/notify-chip-value.components';
import { IdleService } from "@core/services/idle.service";

interface FlashState {
  code: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-authenticate-page-login",
  templateUrl: "./login.component.html",
  styleUrl: "login.component.scss",
  providers: [ provideTranslationScope("feature/auth/login") ],
  imports: [ ReactiveFormsModule,
    MatButtonModule,
    CapsLockNoticeComponent,
    MatFormFieldModule,
    InvalidCredentialsNoticeComponent,
    IconModule,
    HeadlineTitleComponent,
    HeadlineDescriptionComponent,
    HeadlineComponent,
    MatInputModule,
    MatCheckboxModule,
    TranslationPipeModule,
    NotifyChipComponent,
    NotifyChipValueComponent ],
  standalone: true
})
export class LoginComponent {
  private readonly _authService: AuthService = inject(AuthService);

  private readonly _idleService: IdleService = inject(IdleService);

  private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private readonly _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  protected readonly formGroup: FormGroup<{
    email: FormControl<string | null>;
    password: FormControl<string | null>;
    remember: FormControl<boolean | null>;
  }> = new FormGroup<{
      email: FormControl<string | null>;
      password: FormControl<string | null>;
      remember: FormControl<boolean | null>;
    }>({
      /* eslint-disable @tseslint/unbound-method */
      email: new FormControl("", [ Validators.required ]),
      password: new FormControl("", [ Validators.required ]),
      /* eslint-enable @tseslint/unbound-method */
      remember: new FormControl(false)
    });

  protected readonly hidePassword: WritableSignal<boolean> = signal(true);

  protected readonly isLoading: WritableSignal<boolean> = signal(false);

  protected readonly timeoutLogout: WritableSignal<boolean> = signal(false);


  protected readonly flash: WritableSignal<FlashState | null> = signal<FlashState | null>(null);

  constructor () {
    this._faIconLibrary.addIcons(faExclamationCircle, faTimes, faEye, faEyeSlash, faSignOutAlt, faPersonCircleCheck);

    const isInactivityLogout: boolean = this._idleService.hasIdlePath();

    this.timeoutLogout.set(isInactivityLogout);

    const flash: FlashState | undefined = (history.state as { flash?: FlashState; } | undefined)?.flash;

    if (flash) {
      this.flash.set(flash);
      queueMicrotask(() => {
        try {
          history.replaceState({}, document.title);
        } catch {}
      });
    }
  }

  protected handleResetOfControl (controlName: string): void {
    this.formGroup.get(controlName)?.setValue("");
  }

  protected handleToggleOfPassword (): void {
    this.hidePassword.set(!this.hidePassword());
  }

  protected handleClickForget (): void {
    this._authService.navigateToForgotPassword(`${ CORE_ROUTES.AUTHENTICATE }/${ AUTHENTICATE_ROUTES.LOGIN }`);
  }

  protected handleSubmit (): void {
    if (!this.formGroup.valid)
      return;

    const email: string | null | undefined = this.formGroup.get("email")?.value;
    const password: string | null | undefined = this.formGroup.get("password")?.value;

    if (!email || !password)
      return;


    const shouldRememberEmail: boolean | undefined | null = this.formGroup.get("remember")?.value;

    this.isLoading.set(true);
    this._authService.login(email, password, Boolean(shouldRememberEmail)).subscribe({
      error: (httpErrorResponse: HttpErrorResponse) => {
        this.isLoading.set(false);

        if (httpErrorResponse.status === 401) {
          this.formGroup.setErrors({
            error: "credentials"
          });
          this._changeDetectorRef.markForCheck();
        }
      }
    });
  }
}

